import { logger } from '../../core/utils/logger';
import React, { ReactNode } from 'react';
import ErrorBoundary from './ErrorBoundary';

interface AdminErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error) => void;
}

const AdminErrorBoundary: React.FC<AdminErrorBoundaryProps> = ({
  children,
  fallback,
  onError
}) => {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    logger.error('Admin portal error:', error);
    
    if (onError) {
      onError(error);
    }

    if (process.env.NODE_ENV === 'production') {
      fetch('/api/admin/errors', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          error: error.message,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          timestamp: new Date().toISOString(),
          context: 'admin-portal'
        })
      }).catch(err => {
        logger.error('Failed to log admin error:', err);
      });
    }
  };

  const adminFallback = fallback || (
    <div className="min-h-screen flex items-center justify-center bg-admin-color-primary-50 dark:bg-admin-color-primary-900">
      <div className="max-w-md w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg p-6">
        <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 dark:bg-red-900 rounded-full mb-4">
          <svg
            className="w-6 h-6 text-red-600 dark:text-red-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
        </div>
        
        <div className="text-center">
          <h1 className="text-xl font-semibold text-admin-color-primary-900 dark:text-admin-color-primary-100 mb-2">
            Admin Portal Error
          </h1>
          
          <p className="text-admin-color-primary-700 dark:text-admin-color-primary-300 mb-6">
            An error occurred in the admin portal. Please refresh the page or contact support if the issue persists.
          </p>

          <div className="flex space-x-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-admin-color-primary-600 text-white rounded-md hover:bg-admin-color-primary-700 focus:outline-none focus:ring-2 focus:ring-admin-color-primary-500 focus:ring-offset-2 transition-colors"
            >
              Refresh Page
            </button>
            
            <button
              onClick={() => window.location.href = '/admin'}
              className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <ErrorBoundary
      fallback={adminFallback}
      onError={handleError}
      resetOnPropsChange={true}
    >
      {children}
    </ErrorBoundary>
  );
};

export default AdminErrorBoundary;
